import React, { createContext, useState } from 'react';
import axios from 'axios';
import config from '../config/config'

export const OperatorContext = createContext();

export function OperatorProvider(props) {

  const [operators, setOperators] = useState([])
  const [operatorsSearch, setOperatorsSearch] = useState([])
  const [operatorCount, setOperatorCount] = useState(0)
  const [operatorListings, setOperatorListings] = useState([])
  const [page, setPage] = useState(1)

  const getAllOperators = async (page) => {
    try {
      // ponytail: operator paginated route has no `all` branch; 10000 matches getAllUsers/getHospitals pickers. Ceiling: operators beyond 10000 are omitted; upgrade is a server `count === 'all'` find-all.
      const response = await axios.get(`${config.apiUrl}/api/operator/paginated/${page}/10000`);
      return response.data;
    } catch (err) {
      console.log(err);
    }
  }

  const getOperators = async (page) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/operator/paginated/${page}/15`);
      setOperators(response.data);

    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const getOperatorCount = async () => {
    try {
        const response = await axios.get(`${config.apiUrl}/api/operator/count`);
        setOperatorCount(response.data);
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const searchOperator = async (string) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/operator/search/${string}`);
      setOperatorsSearch(response.data);
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const createOperator = async (data, searchString = '') => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/operator/create`, { ...data });
      if (searchString) {
        await searchOperator(searchString).catch(() => {});
      } else {
        await getOperators(page).catch(() => {})
      }
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const editOperator = async (id, data, searchString = '') => {
    try {
      const response = await axios.put(`${config.apiUrl}/api/operator/edit/${id}`, data);
      if (searchString) {
        await searchOperator(searchString).catch(() => {});
      } else {
        await getOperators(page).catch(() => {})
      }
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const publishOperator = async (id, bool, searchString = '') => {
    try {
      const response = await axios.put(`${config.apiUrl}/api/operator/change-publish/${id}`, { publish: bool });
      if (searchString) {
        await searchOperator(searchString).catch(() => {});
      } else {
        await getOperators(page).catch(() => {})
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const getListingByOperator = async (opId) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/listing/operator-cms/${opId}`)
      let data = response.data
      setOperatorListings(data)
    } catch (error) {
      console.log(error)
    }
  }

  const getSupportRequests = async (status) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/support-requests`, {
        params: status ? { status } : {},
      });
      return response.data.requests || [];
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const updateSupportRequestStatus = async (id, status) => {
    try {
      const response = await axios.patch(`${config.apiUrl}/api/support-requests/${id}`, { status });
      return response.data.request;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const getAdminBuildings = async (organization_id) => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/api/building/admin?organization_id=${encodeURIComponent(organization_id)}`
      );
      return {
        buildings: response.data.buildings || [],
        subscriptions: response.data.subscriptions || [],
        custom_billing: Boolean(response.data.custom_billing),
      };
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const updateBuildingLicense = async (organization_id, building_id, { access, renewal_date } = {}) => {
    try {
      const response = await axios.put(
        `${config.apiUrl}/api/building/admin/${building_id}/license`,
        {
          organization_id,
          access,
          renewal_date: renewal_date || null,
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const createAdminBuilding = async (organization_id, data = {}) => {
    try {
      const {
        name,
        status,
        address,
        city,
        state,
        zip,
        phone,
      } = data;
      const response = await axios.post(`${config.apiUrl}/api/building/admin`, {
        organization_id,
        name,
        status,
        address,
        city,
        state,
        zip,
        phone,
      });
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const updateAdminBuilding = async (organization_id, building_id, data = {}) => {
    try {
      const response = await axios.patch(
        `${config.apiUrl}/api/building/admin/${building_id}`,
        {
          organization_id,
          name: data.name,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          phone: data.phone,
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const archiveAdminBuilding = async (organization_id, building_id) => {
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/building/admin/${building_id}/archive`,
        { organization_id }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const deleteAdminBuilding = async (organization_id, building_id) => {
    try {
      const response = await axios.delete(
        `${config.apiUrl}/api/building/admin/${building_id}?organization_id=${encodeURIComponent(organization_id)}`
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  const deleteOperator = async (id, additionalData = {}, searchString = '') => {
    try {
      const response = await axios.post(`${config.apiUrl}/api/operator/delete/${id}`, additionalData);
      if (searchString) {
        await searchOperator(searchString).catch(() => {});
      } else {
        await getOperators(page).catch(() => {})
      }
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  return (
    <OperatorContext.Provider
      value={{
        getAllOperators,
        getOperators,
        operators,
        getOperatorCount,
        operatorCount,
        searchOperator,
        operatorsSearch,
        createOperator,
        editOperator,
        publishOperator,
        getListingByOperator,
        operatorListings,
        getSupportRequests,
        updateSupportRequestStatus,
        getAdminBuildings,
        createAdminBuilding,
        updateAdminBuilding,
        archiveAdminBuilding,
        deleteAdminBuilding,
        updateBuildingLicense,
        deleteOperator,
        page, 
        setPage
      }}
    >
      {props.children}
    </OperatorContext.Provider>
  );
}
